import { useEffect, useState } from "react";
import styles from "@/components/library/BookDetailVisual.module.css";
import { Button } from "@/components/ax/Button";
import { useFlash } from "@/hooks/useFlash";
import { useI18n } from "@/i18n";
import { getSourceBrief } from "@/lib/briefStore";
import { saveInsight, applyInsight } from "@/lib/insightStore";
import { logLibraryEvent } from "@/lib/libraryAnalytics";
import { ApplyDecisionSheet } from "@/components/library/ApplyDecisionSheet";

export const BRIEF_COPY = {
  id: {
    title: "Ringkasan Eksekutif",
    editorNote: "Ringkasan editor AXORIS. Bukan inferensi AI.",
    aiNote: "Bagian ini inferensi AI atas sumber berlisensi.",
    version: "Versi {n}",
    save: "Simpan",
    apply: "Terapkan",
    retry: "Coba lagi",
    failLoad: "Ringkasan belum dapat dimuat.",
    saved: "Disimpan ke insight.",
    applied: "Diterapkan sebagai ASSUMED.",
    saveFail: "Insight belum tersimpan. Coba lagi.",
    layer_source_summary: "Ringkasan sumber",
    layer_editor_summary: "Ringkasan editor",
    layer_ai_inference: "Inferensi AI",
    sections: {
      whyThisMatters: "Mengapa penting",
      coreThesis: "Tesis inti",
      keyPrinciples: "Prinsip kunci",
      decisionFrameworks: "Kerangka keputusan",
      strategicWarnings: "Peringatan strategis",
      assumptions: "Asumsi",
      limitations: "Keterbatasan",
      whenUseful: "Kapan berguna",
    },
  },
  en: {
    title: "Executive Brief",
    editorNote: "An AXORIS editor summary. Not AI inference.",
    aiNote: "This section is AI inference over a licensed source.",
    version: "Version {n}",
    save: "Save",
    apply: "Apply",
    retry: "Try again",
    failLoad: "The brief could not be loaded.",
    saved: "Saved to insights.",
    applied: "Applied as ASSUMED.",
    saveFail: "The insight was not saved. Try again.",
    layer_source_summary: "Source summary",
    layer_editor_summary: "Editor summary",
    layer_ai_inference: "AI inference",
    sections: {
      whyThisMatters: "Why this matters",
      coreThesis: "Core thesis",
      keyPrinciples: "Key principles",
      decisionFrameworks: "Decision frameworks",
      strategicWarnings: "Strategic warnings",
      assumptions: "Assumptions",
      limitations: "Limitations",
      whenUseful: "When useful",
    },
  },
};

const SECTION_ORDER = [
  "whyThisMatters",
  "coreThesis",
  "keyPrinciples",
  "decisionFrameworks",
  "strategicWarnings",
  "assumptions",
  "limitations",
  "whenUseful",
];

const layerClass = (layer) =>
  layer === "editor_summary" ? styles.layerEditor : layer === "ai_inference" ? styles.layerAi : styles.layerSource;

/**
 * Seksi Ringkasan Eksekutif di detail buku open (L11).
 * Hanya dirender bila buku origin open + tidak terkunci (induk mengatur).
 */
export function BriefSection({ book, onChanged }) {
  const { lang } = useI18n();
  const copy = BRIEF_COPY[lang === "en" ? "en" : "id"];
  const [state, setState] = useState("loading");
  const [brief, setBrief] = useState(null);
  const [pending, setPending] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [flash, setFlash] = useFlash(2000);
  const [failed, setFailed] = useState(false);

  const load = async () => {
    setState("loading");
    setFailed(false);
    const res = await getSourceBrief(book.id);
    if (res.error) {
      setFailed(true);
      return setState("error");
    }
    setBrief(res.brief);
    setState("ready");
    void logLibraryEvent("executive_brief_view", {
      bookId: book.id,
      version: res.brief.version,
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book.id]);

  const onSave = async (point) => {
    const pid = `${point.pointId}`;
    setSavingId(pid);
    setFailed(false);
    const res = await saveInsight({
      bookId: book.id,
      sourceId: brief.sourceId || book.sourceId || undefined,
      text: point.text,
      briefVersion: brief.version,
      pointId: point.pointId,
    });
    setSavingId(null);
    if (res.error) return setFailed(true);
    setFlash(copy.saved);
    onChanged?.();
  };

  const onApply = async (decisionId) => {
    if (!pending) return { error: "invalid" };
    const res = await applyInsight({
      bookId: book.id,
      sourceId: brief.sourceId || book.sourceId || undefined,
      text: pending.text,
      briefVersion: brief.version,
      pointId: pending.pointId,
      decisionId,
    });
    if (res.error) return res;
    setPending(null);
    setFlash(copy.applied);
    onChanged?.();
    return {};
  };

  if (state === "loading") {
    return (
      <section aria-busy="true" data-testid="brief-loading" className={styles.briefSection}>
        <p className={styles.briefLoading}>…</p>
      </section>
    );
  }

  if (state !== "ready" || !brief) {
    return (
      <section role="alert" data-testid="brief-error" className={styles.briefSection}>
        <p className="m-0 text-sm text-ax-text-2">{copy.failLoad}</p>
        <div className="mt-3">
          <Button variant="secondary" onClick={load} data-testid="brief-retry-button">{copy.retry}</Button>
        </div>
      </section>
    );
  }

  const isEditor = brief.generationMode !== "ai_assisted";

  return (
    <section data-testid="brief-section" className={styles.briefSection} aria-labelledby="brief-heading">
      <div className={styles.briefHeader}>
        <h2 id="brief-heading" className={styles.briefTitle} data-testid="brief-title">{copy.title}</h2>
        <span className={styles.briefBadge} data-testid="brief-version">
          {copy.version.replace("{n}", String(brief.version))}
        </span>
      </div>
      <p className="m-0 mt-2 text-sm leading-5 text-ax-text-2" data-testid="brief-mode-note">
        {isEditor ? copy.editorNote : copy.aiNote}
      </p>
      <p role="status" data-testid="brief-flash" className={styles.briefFlash}>{flash}</p>
      {failed && <p role="alert" data-testid="brief-save-error" className={styles.briefError}>{copy.saveFail}</p>}

      {SECTION_ORDER.map((section) => {
        const points = brief.content?.[section] ?? [];
        if (!Array.isArray(points) || points.length === 0) return null;
        return (
          <div key={section} className={styles.briefGroup}>
            <h3 className={styles.briefGroupTitle}>{copy.sections[section]}</h3>
            <ul className={styles.briefPoints}>
              {points.map((p) => (
                <li key={p.pointId} data-testid={`brief-point-${p.pointId}`} className={styles.briefPoint}>
                  <p className={styles.briefPointText}>{p.text}</p>
                  <div className={styles.briefPointMeta}>
                    <span data-testid={`brief-layer-${p.pointId}`} className={`${styles.layerBadge} ${layerClass(p.layer)}`}>
                      {copy[`layer_${p.layer}`] ?? copy.layer_source_summary}
                    </span>
                    <div className={styles.briefActions}>
                      <button
                        type="button"
                        data-testid={`brief-save-${p.pointId}`}
                        disabled={savingId === p.pointId}
                        onClick={() => onSave(p)}
                        className={styles.briefAction}
                      >
                        {copy.save}
                      </button>
                      <button
                        type="button"
                        data-testid={`brief-apply-${p.pointId}`}
                        onClick={() => setPending(p)}
                        className={styles.briefAction}
                      >
                        {copy.apply}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <ApplyDecisionSheet
        open={pending !== null}
        onClose={() => setPending(null)}
        onApply={onApply}
        onApplied={() => {}}
        testPrefix="brief-apply"
      />
    </section>
  );
}
